import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Typography, CircularProgress, Box, IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import axios from "axios";
import CustomDataTable from "../../../components/customTable/CustomDataTable";
import { getFormattedDate } from "../../../utils/commonFunctions";
import CopyToClipboardWrapper from "../../../components/copyToClipboardWrapper/CopyToClipboardWrapper";
import Loader from "../../../components/loader/Loader";

const apiEndpoint = process.env.REACT_APP_API_BASE_URL;

interface PaymentTransaction {
  PaymentTransactionId: string;
  IdentityUserId: string;
  TotalAmount: number;
  Cart: Array<{
    Descriptor: string;
    Quantity: number;
    Price: number;
    Source: string;
    SourceId: string;
    Metadata?: any;
  }>;
  PaymentMethods: Array<{
    PaymentType: string;
    Amount: number;
    PaymentChargeId: string;
  }>;
  TransactionDate: string;
  TransactionId: string;
  PendingTransactionId: string;
  Source: string;
  UserName: string;
  MetaData: any;
  TransactionType: string;
  StatementDescriptor: string;
  TransactionFee: number;
  FingerPrint: string;
  SourceId: string;
  AmountRefunded: number;
  RefundTransactionIds: string[] | null;
  StatusTransition: {
    Initiated?: string;
    Processing?: string;
    Successful?: string;
    Failed?: string;
  };
}

const UserPaymentsPage: React.FC = () => {
  const { userId } = useParams<any>();
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPaymentTransactions = async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${apiEndpoint}/paymentgateway/v1/payments/transactions/${userId}`,
        {
          headers: {
            Authorization: `bearer ${accessToken}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (
        response.data &&
        response.data.Transactions &&
        Array.isArray(response.data.Transactions)
      ) {
        setTransactions(response.data.Transactions);
      } else {
        setTransactions([]);
      }
    } catch (err: any) {
      console.error("Failed to fetch payment transactions:", err);
      setError(
        err.response?.data?.message || "Failed to fetch payment transactions"
      );
      setTransactions([]);
    } finally {
      setLoading(false);
      setRefreshLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshLoading(true);
    fetchPaymentTransactions();
  };

  useEffect(() => {
    fetchPaymentTransactions();
  }, [userId]);

  const paymentColumns: GridColDef[] = [
    {
      field: "PaymentTransactionId",
      headerName: "Transaction ID",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 200,
      renderCell: (params: GridRenderCellParams<PaymentTransaction>) => {
        const transactionId = params.row.PaymentTransactionId;
        const lastPart = transactionId ? transactionId.split("-").pop() : "";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span>{lastPart}</span>
            <CopyToClipboardWrapper
              text={transactionId}
              cell="Transaction ID"
            />
          </div>
        );
      }
    },
    {
      field: "TransactionDate",
      headerName: "Transaction Date",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 200,
      renderCell: (params: GridRenderCellParams<PaymentTransaction>) => (
        <span>{getFormattedDate(params.row.TransactionDate)}</span>
      )
    },
    {
      field: "TotalAmount",
      headerName: "Total Amount",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 120,
      renderCell: (params: GridRenderCellParams<PaymentTransaction>) => (
        <span>${(params.row.TotalAmount / 100).toFixed(2)}</span>
      )
    },
    {
      field: "Status",
      headerName: "Status",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 120,
      renderCell: (params: GridRenderCellParams<PaymentTransaction>) => {
        const status = getTransactionStatus(params.row.StatusTransition);
        return (
          <span
            style={{
              padding: "4px 8px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: "500",
              backgroundColor: getStatusColor(status).bg,
              color: getStatusColor(status).text
            }}
          >
            {status}
          </span>
        );
      }
    },
    {
      field: "Source",
      headerName: "Source",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 150
    },
    {
      field: "Cart",
      headerName: "Items",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 200,
      renderCell: (params: GridRenderCellParams<PaymentTransaction>) => (
        <span>{params.row.Cart?.[0]?.Descriptor || "N/A"}</span>
      )
    },
    {
      field: "PaymentMethods",
      headerName: "Payment Methods",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 350,
      renderCell: (params: GridRenderCellParams<PaymentTransaction>) => (
        <div>
          {params.row.PaymentMethods?.map((method, index) => (
            <div key={index} style={{ marginBottom: "4px" }}>
              {method.PaymentType}: ${(method.Amount / 100).toFixed(2)}
            </div>
          ))}
        </div>
      )
    }
  ];

  const getTransactionStatus = (statusTransition: any) => {
    if (statusTransition?.Successful) return "Successful";
    if (statusTransition?.Processing) return "Processing";
    if (statusTransition?.Initiated) return "Initiated";
    if (statusTransition?.Failed) return "Failed";
    return "Unknown";
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "successful":
      case "completed":
      case "success":
        return { bg: "#e8f5e8", text: "#2e7d32" };
      case "processing":
      case "pending":
        return { bg: "#fff3e0", text: "#f57c00" };
      case "failed":
      case "error":
        return { bg: "#ffebee", text: "#d32f2f" };
      case "cancelled":
        return { bg: "#f5f5f5", text: "#757575" };
      case "initiated":
        return { bg: "#e3f2fd", text: "#1976d2" };
      default:
        return { bg: "#f5f5f5", text: "#757575" };
    }
  };

  if (loading && !refreshLoading) {
    return <Loader />;
  }

  return (
    <div>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <IconButton
          onClick={handleRefresh}
          disabled={refreshLoading}
          title="Refresh"
        >
          {refreshLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
        </IconButton>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {transactions.length === 0 && !loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography variant="body1" color="textSecondary">
            No payment transactions found
          </Typography>
        </Box>
      ) : (
        <CustomDataTable
          rows={transactions}
          columns={paymentColumns}
          getRowId="PaymentTransactionId"
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 }
            }
          }}
          pageSizeOptions={[10, 25, 50]}
        />
      )}
    </div>
  );
};

export default UserPaymentsPage;
